/* eslint-disable import/no-cycle */

import { v4 as uuidv4 } from 'uuid';
import {
  BADGES_TYPES,
  CONTACT_STATUS,
  GROUP_STATUS,
  MEDIA_TYPES,
  MESSAGE_TARGET,
  MIXED_DATA_TYPE,
  NAVIGATION_PAGES,
  NOTIFICATION_TYPE,
  REQUEST_DIRECTION,
  REQUEST_TYPE,
  SEND_MESSAGE_ACTIONS,
  SHARE_SETTINGS,
} from '@/types/enums';
import {
  ICase,
  IConversationItemDirectList,
  IConversationItemGroupList,
  IConversationMessage,
  IFolder,
  IGroupData,
  IMedia,
  IMixedData,
  INotification,
  IPartner,
  IRequestSpacesContact,
  IRequestSpacesGroup,
  ISpacesContact,
  IWSContactNewAvatar,
  IWSGroupDelete,
  IWSGroupUpdate,
  IWSMessage,
  IWSNoShare,
  IWSReceiveBadges,
  IWSReceiveContacts,
  IWSReceiveConversation,
  IWSReceiveGroup,
  IWSReceiveShare,
  IWSReceiveAlert,
  IWSSendMessage,
  IWSSharedDriveCase,
  IWSSharedDriveFile,
  IWSSharedDriveFolder,
} from '@/types/interfaces';
import LocalStorageService from './LocalStorageService';
import SkeletonService from '@/services/SkeletonService';
import { useSpacesStore } from '@/modules/spaces/store/spacesStore';
import router from '@/router/index';
import { useMainStore } from '@/store/mainStore';
import { useProfileStore } from '@/modules/profile/store/profileStore';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { useAlertsStore } from '@/modules/alerts/store/alertsStore';
import { useMessagesStore } from '@/modules/messages/store/messagesStore';
import clearPhotoCache, { clearPhotoCache2 } from '@/helpers/clearPhotoCache';

let ws: WebSocket | null = null;

let interval: NodeJS.Timeout | null = null;

const RECONNECT_TIME_MS_CONST = 300;

export default class WebsocketService {
  public static callbackFuncOnMessage: any

  private static RECONNECT_TIME_MS = RECONNECT_TIME_MS_CONST

  public static closeSocket() {
    if (ws) {
      this.RECONNECT_TIME_MS = 0;
      if (ws.readyState === 1) {
        ws.close();
        ws = null; // manual clearing old soket because IOS devices doesn`t close soket correctly. It stucks on status CLOSING
        this.reconnectToWebsocket(); // manual reconnect cause IOS devices doesn`t trigger ws.onclose
        console.log('socket is closing');
      } else if (ws.readyState === 0) {
        this.waitForSocketConnection().then(() => this.closeSocket());
      }
    }
  }

  public static connectWS(): Promise<WebSocket> {
    if (!LocalStorageService.getAuthToken()) {
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject('user not auth');
    }
    // store ?
    return new Promise(((resolve, reject) => {
      ws = new WebSocket(`${process.env.VUE_APP_WS_URL}?authToken=${LocalStorageService.getAuthToken()}`);

      ws.onopen = () => {
        const messagesStore = useMessagesStore();
        if (ws) {
          this.listenMessages();
          console.log('connectWS', messagesStore.currentConversation);
          this.syncChat();
          this.setKeepAliveTimer();
          resolve(ws as WebSocket);
        }
      };

      ws.onerror = (err) => {
        this.removeKeepAliveTimer();
        this.reconnectToWebsocket();
      };

      ws.onclose = () => {
        this.removeKeepAliveTimer();
        this.reconnectToWebsocket();
      };
    }));
  }

  public static getWS(): WebSocket | null {
    return ws;
  }

  private static reconnectToWebsocket() {
    const messagesStore = useMessagesStore();
    messagesStore.setMockMessageError();
    console.log('socket reconnect func');
    if (ws?.readyState === 2) {
      setTimeout(() => {
        this.reconnectToWebsocket();
      }, this.RECONNECT_TIME_MS);
    } else if (ws?.readyState === 3 || !ws) {
      console.log('reconnecting');
      this.connectWS();
      this.RECONNECT_TIME_MS = RECONNECT_TIME_MS_CONST;
    }
  }

  private static async waitForSocketConnection() {
    return new Promise<void>((resolve) => {
      const checkStatus = () => {
        if (ws?.readyState === 1) {
          resolve();
        } else {
          setTimeout(() => {
            checkStatus();
          }, 5);
        }
      };

      checkStatus();
    });
  }

  private static async sendWebsocketMessage(msg: string) {
    await this.waitForSocketConnection();
    if (ws) {
      ws.send(msg);
    }
  }

  private static toWSMessage(
    contactId: string,
    message: string,
    action: SEND_MESSAGE_ACTIONS,
    messageId?: string,
    attachmentId?: string,
    msgType = 'CASE',
    isGroup?: boolean,
    uuid?: string,
    shareSettings?: SHARE_SETTINGS[],
  ): IWSSendMessage {
    const wsMsg: IWSSendMessage = {
      partnerId: contactId,
      content: message,
      action,
      messageId,
      targetType: isGroup ? MESSAGE_TARGET.GROUP : MESSAGE_TARGET.PERSONAL,
      shareSettings,
    };
    if (attachmentId) {
      wsMsg.attachmentId = attachmentId;
      wsMsg.type = msgType;
    }
    if (uuid) {
      wsMsg.uuid = uuid;
    }
    return wsMsg;
  }

  public static syncChat() {
    const messageStore = useMessagesStore();
    const currentConversation = JSON.parse(JSON.stringify(messageStore.currentConversation));

    if (currentConversation?.messages?.length) {
      const partnerId = currentConversation.info.id;
      const targetType = currentConversation.info.isGroup ? MESSAGE_TARGET.GROUP : MESSAGE_TARGET.PERSONAL;
      const messageId = currentConversation.messages[currentConversation.messages.length - 1].id;

      console.log('SYNC_CHAT: ', {
        partnerId, messageId, targetType, action: 'SYNC_MESSAGES',
      });
      this.sendWebsocketMessage(JSON.stringify({
        partnerId, messageId, targetType, action: 'SYNC_MESSAGES',
      }));
    }
  }

  public static sendMedia(contactId: string, attachmentId: string, isGroup?: boolean, shareSettings?: SHARE_SETTINGS[]): void {
    this.sendWebsocketMessage(JSON.stringify(this.toWSMessage(contactId, '', SEND_MESSAGE_ACTIONS.SEND, undefined, attachmentId, 'MEDIA', isGroup, undefined, shareSettings)));
  }

  public static sendFolder(contactId: string, folderId: string, isGroup?: boolean): void {
    this.sendWebsocketMessage(JSON.stringify(this.toWSMessage(contactId, '', SEND_MESSAGE_ACTIONS.SEND, undefined, folderId, 'FOLDER', isGroup)));
  }

  public static sendMessage(contactId: string, message: string, isGroup?: boolean): void {
    const uuid = uuidv4() as string;
    const messagesStore = useMessagesStore();

    messagesStore.addMockMessage(contactId, message, uuid);
    this.sendWebsocketMessage(JSON.stringify(this.toWSMessage(contactId, message, SEND_MESSAGE_ACTIONS.SEND, undefined, undefined, undefined, isGroup, uuid)));
  }

  public static sendCase(contactId: string, caseId: string, message: string, isGroup?: boolean, shareSettings?: SHARE_SETTINGS[]): void {
    this.sendWebsocketMessage(JSON.stringify(this.toWSMessage(contactId, message, SEND_MESSAGE_ACTIONS.SEND, undefined, caseId, undefined, isGroup, undefined, shareSettings)));
  }

  private static setKeepAliveTimer() {
    interval = setInterval(() => {
      this.sendWebsocketMessage(JSON.stringify({ action: 'KEEP_ALIVE' }));
    }, 10000); // 10000 ms = 10 sec, default was 15000 ms = 15 seconds
  }

  private static removeKeepAliveTimer() {
    if (interval) {
      clearInterval(interval);
    } else {
      interval = null;
    }
  }

  private static updateBadge(msg: IWSReceiveBadges): void {
    const mainStore = useMainStore();

    switch (msg.badgeType) {
      case BADGES_TYPES.MESSAGES:
        mainStore.badgesMessage = msg.badgeAmount as number;
        break;
      case BADGES_TYPES.CONTACT_REQUESTS:
        mainStore.badgesContact = msg.badgeAmount as number;
        break;
      case BADGES_TYPES.GROUP_REQUESTS:
        mainStore.badgesGroup = msg.badgeAmount as number;
        break;
      case BADGES_TYPES.IN_GROUP_REQUESTS:
        mainStore.badgesInGroup = msg.badgeAmount as number;
        break;
      case BADGES_TYPES.ALERTS:
        mainStore.badgesAlert = msg.badgeAmount as number;
        break;
      default:
        break;
    }
  }

  private static getCurrentTime(): string {
    return (new Date()).toLocaleString();
  }

  private static removeItemFromNetwork(msg: IWSNoShare) {
    const driveStore = useDriveStore();

    driveStore.networkCases.data = driveStore.networkCases.data.filter((item) => item.id !== msg.body?.unitId);
    driveStore.networkFiles = driveStore.networkFiles.filter((item) => item.id !== msg.body?.unitId);
    driveStore.networkPhotos.data = driveStore.networkPhotos.data.filter((item) => item.id !== msg.body?.unitId);
    driveStore.networkMixedData.data = driveStore.mixedData.data.filter((item) => item.id !== msg.body?.unitId);
  }

  private static toMixedItem(msgs: IWSReceiveShare): IMixedData {
    // eslint-disable-next-line no-nested-ternary
    const type = msgs.body.file ? MIXED_DATA_TYPE.FILE : msgs.body.folder ? MIXED_DATA_TYPE.FOLDER : MIXED_DATA_TYPE.CASE;

    return {
      id: msgs.body.id,
      type,
      case: msgs.body.case,
      file: msgs.body.file,
      folder: msgs.body.folder,
      shared: (new Date(msgs.body.shared)).getTime(),
    } as IMixedData;
  }

  private static updateNetwork(msg: IWSReceiveShare) {
    const driveStore = useDriveStore();

    if (msg.body.file?.type === MEDIA_TYPES.PHOTO) {
      driveStore.networkPhotos.data.unshift(msg.body.file);
    }
    if (msg.body.case) {
      driveStore.networkCases.data.unshift(msg.body.case);
    } else {
      driveStore.mixedData.data.unshift(this.toMixedItem(msg));
    }
  }

  private static updateAlerts(msg: IWSReceiveAlert) {
    const alertsStore = useAlertsStore();
    const alert = msg.notification;

    alertsStore.notifications.data.unshift(alert);
  }

  private static toAlertNotification(msg: IWSReceiveContacts | IWSReceiveGroup, status: NOTIFICATION_TYPE): INotification {
    const partnerId = msg.partnerId?.internalId || (msg.partnerId as unknown as string);
    const accountId = msg.accountId?.internalId || (msg.accountId as unknown as string);
    const partner = msg.partner as unknown as IPartner;
    const { group } = (msg as IWSReceiveGroup);
    return {
      ownerId: {
        internalId: accountId,
      },
      partnerId: {
        internalId: partnerId,
      },
      partner,
      creationDate: msg.timestamp,
      type: status,
      content: group?.name || '',
      sharedInfo: group || null,
      id: msg.notificationId,
      read: false,
    };
  }

  private static toContactInvitation(msg: IWSReceiveContacts): IRequestSpacesContact {
    return {
      creationDate: Date.now(),
      direction: REQUEST_DIRECTION.INCOMING,
      resolutionDate: 0,
      status: 'PENDING',
      user: msg.partner as any as ISpacesContact,
    };
  }

  private static toGroupInvitation(msg: IWSReceiveGroup, type: REQUEST_TYPE): IRequestSpacesGroup {
    return {
      createdTimestamp: Date.now(),
      direction: REQUEST_DIRECTION.INCOMING,
      processedTimestamp: 0,
      status: 'PENDING',
      group: msg.group as any as IGroupData,
      partner: msg.partner as any as IPartner,
      type,
    };
  }

  private static updateContacts(msg: IWSReceiveContacts) {
    const mainStore = useMainStore();
    const profileStore = useProfileStore();
    const spacesStore = useSpacesStore();
    const messagesStore = useMessagesStore();
    const alertsStore = useAlertsStore();

    const navigationPage = router.currentRoute.value.meta?.navigationPage;
    const partnerId = msg.partnerId?.internalId || (msg.partnerId as unknown as string);

    spacesStore.changeSearchContactStatus(partnerId, msg.partner.contactStatus);

    if (msg.status === CONTACT_STATUS.PENDING) {
      const alert = this.toAlertNotification(msg, NOTIFICATION_TYPE.CONTACT_REQUEST_SENT);
      alertsStore.notifications.data.unshift(alert);

      const invitation = this.toContactInvitation(msg);
      spacesStore.invitationsContact.unshift(invitation);

      spacesStore.suggestions = spacesStore.suggestions.filter((sug) => sug.accountId !== partnerId);
    }

    if (msg.status === CONTACT_STATUS.DELETED) {
      spacesStore.myContacts = spacesStore.myContacts.filter((my) => my.user.accountId !== partnerId);
      if (messagesStore.currentConversation?.info?.id === partnerId) {
        messagesStore.currentConversation!.contactStatus = CONTACT_STATUS.NO_CONTACT;
      }

      /** Filter conversations to delete a conversation with a deleted account. */
      messagesStore.conversations = messagesStore.conversations.filter((conv) => {
        if ((conv as IConversationItemDirectList).partner) {
          return (conv as IConversationItemDirectList).partner.accountId !== partnerId;
        }
        return true;
      });
    }

    if (msg.status === CONTACT_STATUS.REJECTED) {
      spacesStore.changeSearchContactStatus(partnerId, CONTACT_STATUS.REJECTED);
      const alert = this.toAlertNotification(msg, NOTIFICATION_TYPE.CONTACT_REQUEST_REJECT);
      alertsStore.notifications.data.unshift(alert);
      spacesStore.outgoingContact = spacesStore.outgoingContact.filter((my) => my.user.accountId !== partnerId);
    }

    if (msg.status === CONTACT_STATUS.ACCEPTED) {
      spacesStore.changeSearchContactStatus(partnerId, CONTACT_STATUS.CONTACT);
      const myContIdx = spacesStore.outgoingContact.findIndex((inv) => inv.user.accountId === partnerId);
      if (myContIdx >= 0) {
        spacesStore.myContacts.push(spacesStore.outgoingContact[myContIdx]);
        spacesStore.outgoingContact.splice(myContIdx, 1);
        spacesStore.suggestions = spacesStore.suggestions.filter((sug) => sug.accountId !== partnerId);
      }
      if (messagesStore.currentConversation?.info?.id === partnerId) {
        messagesStore.currentConversation!.contactStatus = CONTACT_STATUS.CONTACT;
      }
      const alert = this.toAlertNotification(msg, NOTIFICATION_TYPE.CONTACT_REQUEST_ACCEPT);
      alertsStore.notifications.data.unshift(alert);
    }

    if (navigationPage === NAVIGATION_PAGES.ALERTS) {
      mainStore.badgesAlert = 0;
    }

    if (navigationPage === NAVIGATION_PAGES.PROFILE_FOREIGN) {
      profileStore.getProfileById(router.currentRoute.value.params.id as string);
    }
  }

  private static updateGroups(msg: IWSReceiveGroup) {
    const mainStore = useMainStore();
    const spacesStore = useSpacesStore();
    const alertsStore = useAlertsStore();
    const messagesStore = useMessagesStore();

    const navigationPage = router.currentRoute.value.meta?.navigationPage;

    const partnerId = msg.partnerId?.internalId || (msg.partnerId as unknown as string);
    const groupId = msg.group?.id;

    if (msg.status === GROUP_STATUS.PENDING) {
      const alert = this.toAlertNotification(msg, NOTIFICATION_TYPE.GROUP_REQUEST_SENT);
      alertsStore.notifications.data.unshift(alert);

      const invitation = this.toGroupInvitation(msg, REQUEST_TYPE.GROUP_INVITE_REQUEST);
      spacesStore.invitationsGroup.unshift(invitation);

      spacesStore.suggestions = spacesStore.suggestions.filter((sug) => sug.accountId !== partnerId);
    }

    if (msg.status === GROUP_STATUS.DELETED) {
      const alert = this.toAlertNotification(msg, NOTIFICATION_TYPE.REMOVED_FROM_GROUP);
      alertsStore.notifications.data.unshift(alert);

      const currentGroup = spacesStore.myGroups.find((group) => group.id === groupId);
      if (currentGroup) {
        currentGroup.members = currentGroup.members.filter((user) => user.accountId !== mainStore.user.accountId);
      }
    }

    if (msg.status === GROUP_STATUS.REJECTED) {
      const alert = this.toAlertNotification(msg, NOTIFICATION_TYPE.GROUP_REQUEST_REJECT);
      alertsStore.notifications.data.unshift(alert);

      const currentGroup = spacesStore.myGroups.find((group) => group.id === groupId);
      if (currentGroup) {
        currentGroup.invitedUsers = currentGroup.invitedUsers.filter((user) => user.accountId !== partnerId);
      }
    }

    if (msg.status === GROUP_STATUS.ACCEPTED) {
      const alert = this.toAlertNotification(msg, NOTIFICATION_TYPE.GROUP_REQUEST_ACCEPT);
      alertsStore.notifications.data.unshift(alert);

      const currentGroup = spacesStore.myGroups.find((group) => group.id === groupId);
      if (currentGroup) {
        currentGroup.members.push(msg.partner as unknown as ISpacesContact);
      }
    }

    if (msg.status === GROUP_STATUS.IN_GROUP_PENDING) {
      const alert = this.toAlertNotification(msg, NOTIFICATION_TYPE.IN_GROUP_REQUEST_SENT);
      alertsStore.notifications.data.unshift(alert);

      const currentGroup = spacesStore.myGroups.find((group) => group.id === groupId);
      if (currentGroup && messagesStore.currentConversation?.group?.requestsToGroup) {
        messagesStore.currentConversation.group.requestsToGroup.push({
          partner: msg.partner as unknown as IPartner,
          status: GROUP_STATUS.PENDING,
        } as IRequestSpacesGroup);
      }

      const invitation = this.toGroupInvitation(msg, REQUEST_TYPE.IN_GROUP_REQUEST);
      spacesStore.requestsToMyGroups.unshift(invitation);
    }

    if (msg.status === GROUP_STATUS.IN_GROUP_REJECTED) {
      const alert = this.toAlertNotification(msg, NOTIFICATION_TYPE.IN_GROUP_REQUEST_REJECT);
      alertsStore.notifications.data.unshift(alert);

      const currentSearchGroup = spacesStore.searchGroupsList.find((group) => group.id === groupId);
      if (currentSearchGroup) {
        currentSearchGroup.groupStatus = null;
      }
      if (spacesStore.outgoingGroup.length) {
        spacesStore.outgoingGroup = spacesStore.outgoingGroup.filter((group) => group.id !== groupId);
      }
    }

    if (msg.status === GROUP_STATUS.IN_GROUP_ACCEPTED) {
      const alert = this.toAlertNotification(msg, NOTIFICATION_TYPE.IN_GROUP_REQUEST_ACCEPT);
      alertsStore.notifications.data.unshift(alert);

      if (spacesStore.outgoingGroup.length) {
        spacesStore.outgoingGroup = spacesStore.outgoingGroup.filter((group) => group.id !== groupId);
      }
      const currentGroup = spacesStore.myGroups.find((group) => group.id === groupId);
      if (currentGroup) {
        currentGroup.members.push(mainStore.user as unknown as ISpacesContact);
      } else {
        msg.group.members.push(mainStore.user as unknown as ISpacesContact);
        spacesStore.myGroups.unshift(msg.group);
      }

      const currentSearchGroup = spacesStore.searchGroupsList.find((group) => group.id === groupId);
      if (currentSearchGroup) {
        currentSearchGroup.members.push(mainStore.user as unknown as ISpacesContact);
      }
    }

    if (navigationPage === NAVIGATION_PAGES.ALERTS) {
      mainStore.badgesAlert = 0;
    }
  }

  private static toConversationMessage(WSMsg: IWSReceiveConversation): IConversationMessage {
    if ((typeof WSMsg.attachment?.share?.sharedAccounts[0]) === 'string') {
      // eslint-disable-next-line no-param-reassign
      WSMsg.attachment.share!.sharedAccounts = WSMsg.attachment.share!.sharedAccounts.map((id: any) => ({ internalId: id }));
    }

    const msg: IConversationMessage = {
      content: WSMsg.content,
      conversationId: WSMsg.conversationId,
      created: WSMsg.created,
      id: WSMsg.id,
      owner: WSMsg.owner,
      read: WSMsg.isRead,
      type: WSMsg.type,
      attachment: WSMsg.attachment,
      attachmentType: WSMsg.attachmentType,
    };
    return msg;
  }

  private static updateSharingInfo(msg: IWSReceiveConversation) {
    const { attachment } = msg;
    if (!attachment) {
      return;
    }

    if ((attachment as any).ownerId && !(attachment as any).ownerId?.internalId) {
      (attachment as any).ownerId = { internalId: (attachment as any).ownerId };
    }

    const driveStore = useDriveStore();

    const updateFile = (media: IMedia) => {
      driveStore.mixedData.data = driveStore.mixedData.data.map((item) => {
        if (item.file?.contentId === media.contentId) {
          return {
            ...item,
            file: { ...item.file, ...media },
          };
        }
        return item;
      });

      if (media.folderId) {
        driveStore.cases.data = driveStore.cases.data.map((cas) => {
          if (cas.id === media.folderId) {
            return {
              ...cas,
              files: cas.files.map((item) => {
                if (item.contentId === media.contentId) {
                  return { ...item, ...media };
                }
                return item;
              }),
            };
          }
          return cas;
        });
      }
    };

    const updatePhoto = (media: IMedia) => {
      driveStore.mixedData.data = driveStore.mixedData.data.map((item) => {
        if (item.file?.contentId === media.contentId) {
          return {
            ...item,
            file: { ...item.file, ...media },
          };
        }
        return item;
      });

      driveStore.photos.data = driveStore.photos.data.map((item) => {
        if (item?.contentId === media.contentId) {
          return {
            ...item,
            ...media,
          };
        }
        return item;
      });

      driveStore.photoList = driveStore.photoList.map((item) => {
        if (item?.contentId === media.contentId) {
          return {
            ...item,
            ...media,
          };
        }
        return item;
      });

      if (media.folderId) {
        driveStore.cases.data = driveStore.cases.data.map((cas) => {
          if (cas.id === media.folderId) {
            return {
              ...cas,
              photos: cas.photos.map((item) => {
                if (item.contentId === media.contentId) {
                  return { ...item, ...media };
                }
                return item;
              }),
            };
          }
          return cas;
        });
      }
    };

    const updateFolder = (folder: IFolder) => {
      driveStore.mixedData.data = driveStore.mixedData.data.map((item) => {
        if (item.id === folder.id) {
          return {
            ...item,
            folder,
          };
        }
        return item;
      });
    };

    const updateCase = (iCase: ICase) => {
      driveStore.mixedData.data = driveStore.mixedData.data.map((item) => {
        if (item.id === iCase.id) {
          return {
            ...item,
            case: iCase,
          };
        }
        return item;
      });

      driveStore.cases.data = driveStore.cases.data.map((item) => {
        if (item.id === iCase.id) {
          return iCase;
        }
        return item;
      });
    };

    if ((attachment as IMedia).type) {
      const media = attachment as IMedia;
      if (media.type === MEDIA_TYPES.FILES) {
        updateFile(media);
      } else {
        updatePhoto(media);
      }
    } else if ((attachment as IFolder).folderType) {
      const folder = attachment as IFolder;
      updateFolder(folder);
    } else {
      const iCase = (attachment as ICase);
      updateCase(iCase);
    }
  }

  private static addMessageToConversation(msg: IWSReceiveConversation): void {
    console.log('WS addMessageToConversation: ', msg);
    const messagesStore = useMessagesStore();

    this.updateSharingInfo(msg);
    const convMsg = this.toConversationMessage(msg);

    const { currentDialogsSkeletons } = SkeletonService.getSkeletonInfo();
    if (currentDialogsSkeletons[msg.conversationId.split('_')[1]]) {
      SkeletonService.removeDialogSkeletons(msg.conversationId.split('_')[1], 1, MEDIA_TYPES.FILES);
    }

    messagesStore.removeMockMessage(msg.conversationId.split('_')[1], msg.uuid);
    messagesStore.addMessageToConversation(convMsg);
  }

  private static removeUnsharedMedia(msg: IWSNoShare): void {
    const messagesStore = useMessagesStore();
    const messages = messagesStore!.currentConversation?.messages || [];
    for (let m = 0; m < messages.length; m += 1) {
      if (messages[m]?.attachment?.id === msg.unitId) {
        messagesStore!.currentConversation!.messages[m].attachment = null;
        messagesStore!.currentConversation!.messages[m].content = `MEDIA from ${messages[m]?.owner?.firstName} ${messages[m]?.owner?.lastName} is not shared anymore `;
      }
    }
  }

  private static updateGroupChatInfo(msg: IWSGroupUpdate): void {
    const messagesStore = useMessagesStore();
    const mainStore = useMainStore();
    const spacesStore = useSpacesStore();

    const conv = messagesStore.conversations.find((cv) => ((cv as IConversationItemGroupList)?.group?.id === msg.body.id));
    if (conv) {
      (conv as IConversationItemGroupList).group = msg.body;
    } else {
      messagesStore.fetchConversations();
    }
    messagesStore.fetchGroupsForChat();

    if (messagesStore.currentConversation?.info.id === msg.body.id) {
      messagesStore.currentConversation!.group = msg.body;
      messagesStore.currentConversation.info = {
        ...messagesStore.currentConversation.info,
        name: msg.body.name,
        photo: msg.body.photo,
      };

      const stillGroupMember = messagesStore.currentConversation!.group?.members.find((member) => member.accountId === mainStore.user.accountId);
      if (!stillGroupMember) {
        router.push('/messages/my');
      }
    }

    /** Updates the group in the spacesStore cache. */
    const groupIdx = spacesStore.myGroups.findIndex((group) => group.id === msg.body.id);
    if (groupIdx >= 0) {
      spacesStore.myGroups[groupIdx] = msg.body;
    }
  }

  public static deleteGroupChatInfo(msg: IWSGroupDelete): void {
    const messagesStore = useMessagesStore();

    if (messagesStore.currentConversation?.group?.id === msg.body) {
      router.push('/messages/my');
    } else {
      messagesStore.fetchConversations();
      messagesStore.fetchGroupsForChat();
    }
  }

  public static readMessage(contactId: string, message: string, messageId: string, to?: string): void {
    if (ws) {
      const wsMsg = this.toWSMessage(contactId, message, SEND_MESSAGE_ACTIONS.READ, messageId, undefined, undefined, !!to);
      wsMsg.to = to;
      this.sendWebsocketMessage(JSON.stringify(wsMsg));
    }
  }

  private static updateContactAvatar(msg: IWSContactNewAvatar) {
    const spacesStore = useSpacesStore();
    const messagesStore = useMessagesStore();

    const contact = spacesStore.myContacts.find((cont) => cont.user.accountId === msg.accountId);
    if (contact) {
      clearPhotoCache2(contact.user.photo);
      //  = msg.avatarUrl;
    }

    if (messagesStore.currentConversation) {
      messagesStore.currentConversation.messages = messagesStore.currentConversation.messages.map((message) => {
        if (message.owner.accountId === msg.accountId) {
          clearPhotoCache2(message.owner.photo);
        }
        return message;
      });
      if (messagesStore.currentConversation.info.photo?.url === msg.avatarUrl) {
        clearPhotoCache2(messagesStore.currentConversation.info.photo);
      }
    }

    messagesStore.conversations = messagesStore.conversations.map((conv) => {
      if ((conv as IConversationItemDirectList)?.partner?.accountId === msg.accountId) {
        const photo = (conv as IConversationItemDirectList)?.partner?.photo;
        if (photo) {
          clearPhotoCache2(photo);
        }
      }
      return conv;
    });
  }

  private static updateGroupAvatar(msg: IWSGroupUpdate) {
    const spacesStore = useSpacesStore();
    const messagesStore = useMessagesStore();

    const currentGroup = spacesStore.myGroups.find((group) => group.id === msg.body.id);
    if (currentGroup?.photo?.url) {
      clearPhotoCache2(currentGroup.photo);
    }

    if (messagesStore.currentConversation) {
      messagesStore.currentConversation.messages = messagesStore.currentConversation.messages.map((message) => {
        if (message.owner.accountId === msg.body.ownerId) {
          clearPhotoCache2(message.owner.photo);
        }
        return message;
      });
      if (messagesStore.currentConversation.info.photo?.url === msg.body.photo.url) {
        clearPhotoCache2(messagesStore.currentConversation.info.photo);
      }
    }

    messagesStore.conversations = messagesStore.conversations.map((conv) => {
      if ((conv as IConversationItemGroupList)?.groupId === msg.body.id) {
        const photo = (conv as IConversationItemGroupList)?.group?.photo;
        if (photo) {
          clearPhotoCache2(photo);
        }
      }
      return conv;
    });
  }

  private static updateSharedDriveFile(msg: IWSSharedDriveFile): void {
    const driveStore = useDriveStore();
    if (msg.body.type !== MEDIA_TYPES.FILES) {
      clearPhotoCache(msg.body);
      driveStore.photos.data = driveStore.photos.data.filter((photo) => photo.contentId !== msg.body.contentId);
      driveStore.photos.data = [msg.body, ...driveStore.photos.data];
    }

    if (!msg.body.folderId) {
      const mixed = {
        id: msg.body.id,
        type: MIXED_DATA_TYPE.FILE,
        file: msg.body,
      } as IMixedData;
      driveStore.mixedData.data = [mixed, ...driveStore.mixedData.data];
    }
  }

  private static updateSharedDriveCase(msg: IWSSharedDriveCase): void {
    const driveStore = useDriveStore();
    driveStore.cases.data = [msg.body, ...driveStore.cases.data];

    if (!msg.body.folderId) {
      const mixed = {
        id: msg.body.id,
        type: MIXED_DATA_TYPE.CASE,
        case: msg.body,
      } as IMixedData;
      driveStore.mixedData.data = [mixed, ...driveStore.mixedData.data];
    }
  }

  private static updateSharedDriveFolder(msg: IWSSharedDriveFolder): void {
    const driveStore = useDriveStore();

    if (!msg.body.upperFolderId) {
      const mixed = {
        id: msg.body.id,
        type: MIXED_DATA_TYPE.FOLDER,
        folder: msg.body,
      } as IMixedData;
      driveStore.mixedData.data = [mixed, ...driveStore.mixedData.data];
    }
  }

  private static listenMessages(): void {
    ws!.onmessage = (response) => {
      const msgResonse: IWSMessage = JSON.parse(response.data);
      if (msgResonse.success) {
        const msgs = msgResonse.results.messages;
        if (msgs) {
          (msgs as IWSReceiveConversation[]).forEach((msg) => {
            this.addMessageToConversation(msg as IWSReceiveConversation);
          });
        } else {
          const msg = msgResonse.results.message;
          if (this.callbackFuncOnMessage) {
            this.callbackFuncOnMessage(msg);
          }

          switch (msg.action) {
            case SEND_MESSAGE_ACTIONS.SEND:
              this.addMessageToConversation(msg as IWSReceiveConversation);
              break;
            case SEND_MESSAGE_ACTIONS.UPDATE_BADGE:
              this.updateBadge(msg as IWSReceiveBadges);
              break;
            case SEND_MESSAGE_ACTIONS.GROUP_STATUS:
              this.updateGroups(msg as IWSReceiveGroup);
              break;
            case SEND_MESSAGE_ACTIONS.CONTACT_STATUS:
              this.updateContacts(msg as IWSReceiveContacts);
              break;
            case SEND_MESSAGE_ACTIONS.SHARE:
              this.updateNetwork(msg as IWSReceiveShare);
              break;
            case SEND_MESSAGE_ACTIONS.NEW_ALERT:
              this.updateAlerts(msg as IWSReceiveAlert);
              break;
            case SEND_MESSAGE_ACTIONS.NO_SHARE:
              this.removeUnsharedMedia(msg as IWSNoShare);
              this.removeItemFromNetwork(msg as IWSNoShare);
              break;
            case SEND_MESSAGE_ACTIONS.GROUP_UPDATED:
              this.updateGroupChatInfo(msg as IWSGroupUpdate);
              this.updateGroupAvatar(msg as IWSGroupUpdate);
              break;
            case SEND_MESSAGE_ACTIONS.USER_JOIN_IN_GROUP:
              this.updateGroupChatInfo(msg as IWSGroupUpdate);
              break;
            case SEND_MESSAGE_ACTIONS.USER_LEFT_THE_GROUP:
              this.updateGroupChatInfo(msg as IWSGroupUpdate);
              break;
            case SEND_MESSAGE_ACTIONS.GROUP_DELETED:
              this.deleteGroupChatInfo(msg as IWSGroupDelete);
              break;
            case SEND_MESSAGE_ACTIONS.AVATAR_CHANGED:
              this.updateContactAvatar(msg as IWSContactNewAvatar);
              break;
            case SEND_MESSAGE_ACTIONS.SHARED_DRIVE_FILE_SAVED:
              this.updateSharedDriveFile(msg as IWSSharedDriveFile);
              break;
            case SEND_MESSAGE_ACTIONS.SHARED_DRIVE_CASE_SAVED:
              this.updateSharedDriveCase(msg as IWSSharedDriveCase);
              break;
            case SEND_MESSAGE_ACTIONS.SHARED_DRIVE_FOLDER_SAVED:
              this.updateSharedDriveFolder(msg as IWSSharedDriveFolder);
              break;
            default:
              break;
          }
        }
      }
    };
  }
}
